/**
 * TileRowRenderer — the first true primitive renderer in server-ui.
 * Renders a horizontal row of tiles with variant-driven colors,
 * headlines, details, and action buttons.
 *
 * This is a pure primitive renderer with zero domain knowledge.
 * Any consuming feature that doesn't register a domain component
 * for `tile_row` (or for a specific type_key like `intelligence_brief`)
 * gets this renderer via the primitive fallback dispatch.
 *
 * Action wiring is currently STUBBED. The buttons log on click
 * but don't dispatch any backend action. A backend ticket is
 * in flight to build the dispatch endpoint:
 *
 *   POST /v2/provider/containers/{container_key}/components/{type_key}/actions/{action_key}
 *
 * Once that endpoint lands and the codegen picks up the mutation
 * hook, a follow-up PR will replace the click handler stubs
 * with real dispatch.
 *
 * Cards have white backgrounds with a hover lift effect. The tile label
 * renders as a soft Chip whose color is driven by the variant. Only the
 * safety variant adds a left border accent to the card itself.
 *
 * Variant → chip color mapping:
 *   safety  → error
 *   alert   → error
 *   warning → warning
 *   info    → success
 *   neutral → default
 *
 * See .docs/server-driven-ui/architectural-pivot-context-dispatch.md
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollCarousel } from '@components/primitives/ScrollCarousel';
import { patientRemindersApi } from '@features/encounters/store';
import { ThumbsFeedback } from '@features/thumbs-feedback';
import { Chip, type ChipProps } from '@jotpsych/ui';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks/core';
import { useSnackbar } from 'notistack';

import { useDismissComponentMutation, useDispatchTileActionMutation } from '../../store';
import { useInvalidateAndRefetch } from '../../store/hooks';
import type { Tile, TileAction, TileRowData } from '../../types';
import { useServerUiScope } from '../ServerUiDomainProvider';

type TileRowRendererProps = {
  typeKey: string;
  data: TileRowData;
};

// --- Variant → soft chip color mapping ---
// The chip label is the only variant-driven visual on the card.

const VARIANT_CHIP_COLORS: Record<string, ChipProps['color']> = {
  safety: 'error',
  alert: 'error',
  warning: 'warning',
  info: 'success',
  neutral: 'default',
};

// --- Action buttons ---
// Split into two components so each only subscribes to one mutation endpoint.
// Previously a single component held both hooks, causing RTK Query to fire
// invalidatesTags multiple times per click (once per subscriber).

type ActionButtonSharedProps = {
  tileId: string;
  typeKey: string;
  variant: string;
  isPrimary: boolean;
};

type ActionButtonVisualProps = {
  label: string;
  isPrimary: boolean;
  isLoading: boolean;
  onClick: () => void;
};

const ActionButtonVisual: React.FC<ActionButtonVisualProps> = ({
  label,
  isPrimary,
  isLoading,
  onClick,
}) => (
  <Button
    variant={isPrimary ? 'contained' : 'text'}
    color={isPrimary ? 'primary' : 'inherit'}
    size="small"
    disabled={isLoading}
    onClick={onClick}
    sx={{ textTransform: 'none', minWidth: 80 }}
  >
    {isLoading ? <CircularProgress size={16} color="inherit" /> : label}
  </Button>
);

const DismissButton: React.FC<ActionButtonSharedProps> = ({ tileId, typeKey, variant, isPrimary }) => {
  const { containerKey, entityId, surface } = useServerUiScope();
  const { enqueueSnackbar } = useSnackbar();
  const [dismiss] = useDismissComponentMutation();
  const invalidateAndRefetch = useInvalidateAndRefetch();
  const { tile: tileTracking } = useEventTracking();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (!containerKey || !entityId) return;
    tileTracking.actionClicked({
      container_key: containerKey,
      entity_id: entityId,
      type_key: typeKey,
      tile_id: tileId,
      action_key: 'dismiss',
      variant,
      surface,
    });
    setIsLoading(true);
    try {
      await dismiss({ containerKey, typeKey, entityId, tileId }).unwrap();
      await invalidateAndRefetch();
    } catch (err: unknown) {
      const errorType = (err as { status?: string })?.status === 'FETCH_ERROR' ? 'network' : 'api';
      tileTracking.actionFailed({
        container_key: containerKey,
        entity_id: entityId,
        type_key: typeKey,
        tile_id: tileId,
        action_key: 'dismiss',
        variant,
        error_type: errorType,
        surface,
      });
      enqueueSnackbar('Failed to dismiss tile', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ActionButtonVisual
      label="Dismiss"
      isPrimary={isPrimary}
      isLoading={isLoading}
      onClick={handleClick}
    />
  );
};

const DispatchButton: React.FC<ActionButtonSharedProps & { action: TileAction }> = ({
  action,
  tileId,
  typeKey,
  variant,
  isPrimary,
}) => {
  const { containerKey, entityId, surface } = useServerUiScope();
  const { enqueueSnackbar } = useSnackbar();
  const [dispatchAction] = useDispatchTileActionMutation();
  const invalidateAndRefetch = useInvalidateAndRefetch();
  const dispatch = useAppDispatch();
  const { tile: tileTracking } = useEventTracking();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (!containerKey || !entityId) return;
    tileTracking.actionClicked({
      container_key: containerKey,
      entity_id: entityId,
      type_key: typeKey,
      tile_id: tileId,
      action_key: action.action_key,
      variant,
      surface,
    });
    setIsLoading(true);
    try {
      await dispatchAction({
        containerKey,
        typeKey,
        actionKey: action.action_key,
        tileActionRequest: { entity_id: entityId, tile_id: tileId },
      }).unwrap();
      // Tile actions on the patient page (e.g. "Remind in session" on the
      // intelligence brief) can create patient reminders server-side. Drop
      // the cached PatientReminders list so the encounter view's `show_ad`
      // flag refetches and the Memos reminder ad doesn't go stale.
      dispatch(
        patientRemindersApi.util.invalidateTags([
          { type: 'PatientReminders', id: entityId },
        ]),
      );
      await invalidateAndRefetch();
    } catch (err: unknown) {
      const errorType = (err as { status?: string })?.status === 'FETCH_ERROR' ? 'network' : 'api';
      tileTracking.actionFailed({
        container_key: containerKey,
        entity_id: entityId,
        type_key: typeKey,
        tile_id: tileId,
        action_key: action.action_key,
        variant,
        error_type: errorType,
        surface,
      });
      enqueueSnackbar(`Failed to ${action.label.toLowerCase()}`, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ActionButtonVisual
      label={action.label}
      isPrimary={isPrimary}
      isLoading={isLoading}
      onClick={handleClick}
    />
  );
};

type TileActionButtonProps = {
  action: TileAction;
  tileId: string;
  typeKey: string;
  variant: string;
  isPrimary: boolean;
};

const TileActionButton: React.FC<TileActionButtonProps> = ({
  action,
  tileId,
  typeKey,
  variant,
  isPrimary,
}) => {
  if (action.action_key === 'dismiss') {
    return <DismissButton tileId={tileId} typeKey={typeKey} variant={variant} isPrimary={isPrimary} />;
  }
  return <DispatchButton action={action} tileId={tileId} typeKey={typeKey} variant={variant} isPrimary={isPrimary} />;
};

// --- Single tile card ---

type TileCardProps = {
  tile: Tile;
  typeKey: string;
};

const TILE_HOVER_THRESHOLD_MS = 2000;

const TileCard: React.FC<TileCardProps> = ({ tile, typeKey }) => {
  const { containerKey, entityId, surface } = useServerUiScope();
  const { tile: tileTracking } = useEventTracking();
  // Feedback rendering is gated server-side via feature_server_ui_feedback.
  // When the gate is off, tile.feedback is always null — no client-side gate needed.
  const feedback = tile.feedback ?? null;
  const hasLabel = tile.label != null;

  // --- Hover / dwell-time tracking ---
  const hoverStartRef = useRef<number | null>(null);

  const handleMouseEnter = useCallback(() => {
    hoverStartRef.current = performance.now();
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverStartRef.current === null) return;
    const dwell = performance.now() - hoverStartRef.current;
    hoverStartRef.current = null;
    if (dwell >= TILE_HOVER_THRESHOLD_MS && containerKey && entityId) {
      tileTracking.hovered({
        container_key: containerKey,
        entity_id: entityId,
        type_key: typeKey,
        tile_id: tile.id,
        variant: tile.variant,
        dwell_ms: String(Math.round(dwell)),
        surface,
      });
    }
  }, [containerKey, entityId, surface, typeKey, tile.id, tile.variant, tileTracking]);

  // Fire pending hover event on unmount (e.g. tile dismissed while hovered)
  useEffect(() => {
    return () => {
      if (hoverStartRef.current === null) return;
      const dwell = performance.now() - hoverStartRef.current;
      if (dwell >= TILE_HOVER_THRESHOLD_MS && containerKey && entityId) {
        tileTracking.hovered({
          container_key: containerKey,
          entity_id: entityId,
          type_key: typeKey,
          tile_id: tile.id,
          variant: tile.variant,
          dwell_ms: String(Math.round(dwell)),
          surface,
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- tile identity and scope are stable for the component's lifetime; cleanup only needs mount-time values
  }, []);

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        // 3 cards fill ~1200px container: (1200 - 32px padding - 24px gaps) / 3 ≈ 381px
        flex: '0 0 calc((100% - 24px) / 3)',
        minWidth: 280,
        borderRadius: 3, // 12px — matches SectionCard
        px: 2.5,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        bgcolor: 'background.paper',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'scale(1.01)',
          boxShadow: 'rgba(0, 0, 0, 0.06) 0px 4px 20px',
        },
      }}
    >
      {(hasLabel || feedback != null) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          {hasLabel ? (
            <Chip
              label={tile.label}
              variant="soft"
              color={VARIANT_CHIP_COLORS[tile.variant] ?? 'default'}
              size="xSmall"
              sx={{ textTransform: 'uppercase' }}
            />
          ) : (
            <Box />
          )}
          {feedback != null && (
            <ThumbsFeedback
              contentType={feedback.content_type}
              responseId={tile.id}
              compact
              contextIds={{
                ...(containerKey ? { container_key: containerKey } : {}),
                ...(entityId ? { entity_id: entityId } : {}),
                tile_id: tile.id,
              }}
            />
          )}
        </Box>
      )}

      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.4 }}>
        {tile.headline}
      </Typography>

      {tile.detail && (
        <Typography
          sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 }}
          color="text.secondary"
        >
          {tile.detail}
        </Typography>
      )}

      {tile.actions && tile.actions.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 1 }}>
          {tile.actions.map((action, index) => (
            <TileActionButton
              key={action.action_key}
              action={action}
              tileId={tile.id}
              typeKey={typeKey}
              variant={tile.variant}
              isPrimary={index === 0}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

// --- Row renderer ---

export const TileRowRenderer: React.FC<TileRowRendererProps> = ({ typeKey, data }) => {
  const theme = useTheme();

  return (
    <ScrollCarousel
      gap={1.5}
      fadeColor={theme.palette.grey[50]}
      sx={{ maxWidth: 1200, mx: 'auto' }}
      scrollContainerSx={{ px: 2, py: 1.5, my: -1.5, scrollPaddingInline: theme.spacing(2) }}
    >
      {data.tiles.map(tile => (
        <TileCard key={tile.id} tile={tile} typeKey={typeKey} />
      ))}
    </ScrollCarousel>
  );
};
